import {
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons'
import { Button, Col, Form, Input, InputNumber, Row, Space } from 'antd'
import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  BUYBACK_FIELD as FIELD,
  BuybackFee,
} from '../../../common/buybackTypes'
import {
  computeProcessCostsTotal,
  inputNumberFormatter,
} from '../../utils/buybackHelpers'

export const BuybackFormProcessCostsFragment: FunctionComponent = () => {
  const form = Form.useFormInstance()
  const processCostsRaw = Form.useWatch(FIELD.ProcessCosts) as
    | BuybackFee[]
    | undefined

  const processCosts: BuybackFee[] = useMemo(
    () => processCostsRaw ?? [],
    [processCostsRaw]
  )

  const processCostsTotal = useMemo(
    () => computeProcessCostsTotal(processCosts),
    [processCosts]
  )

  useEffect(() => {
    const currentTotal = form.getFieldValue(FIELD.ProcessCostAmountTotal)
    if (currentTotal !== processCostsTotal) {
      form.setFieldValue(FIELD.ProcessCostAmountTotal, processCostsTotal)
    }
  }, [processCostsTotal, form])

  return (
    <Form.List name={FIELD.ProcessCosts}>
      {(fields, { add, remove, move }) => (
        <>
          {fields.map((field, index) => (
            <div key={field.key}>
              <Row gutter={[8, 0]} align="top">
                <Col xs={24} md={13} xl={13}>
                  <Form.Item
                    name={[field.name, FIELD.ProcessCostName]}
                    rules={[{ required: true, message: 'Zadejte název' }]}
                    className="!mb-4"
                  >
                    <Input placeholder="Název poplatku" autoFocus />
                  </Form.Item>
                </Col>
                <Col xs={16} md={7} xl={8}>
                  <Form.Item
                    name={[field.name, FIELD.ProcessCostAmount]}
                    rules={[{ required: true, message: 'Zadejte částku' }]}
                    className="!mb-4"
                  >
                    <InputNumber
                      min={0}
                      step={100}
                      className="w-full"
                      formatter={inputNumberFormatter}
                      addonAfter="Kč"
                    />
                  </Form.Item>
                </Col>
                <Col xs={8} md={4} xl={3} className="!flex gap-2">
                  <Space.Compact className="flex">
                    <Button
                      onClick={() => move(index, index - 1)}
                      disabled={index === 0}
                      icon={<UpOutlined />}
                    />
                    <Button
                      onClick={() => move(index, index + 1)}
                      disabled={index === fields.length - 1}
                      icon={<DownOutlined />}
                    />
                  </Space.Compact>
                  <Button
                    danger
                    className="flex-1"
                    onClick={() => remove(field.name)}
                    disabled={fields.length <= 1}
                    icon={<DeleteOutlined />}
                  />
                </Col>
                <Form.Item name={[field.name, FIELD.ProcessCostId]} hidden>
                  <Input />
                </Form.Item>
              </Row>
            </div>
          ))}
          <Row gutter={[8, 16]} align="middle" justify="end" className="mb-12">
            <Col xs={24} md={13} xl={13}>
              <Input
                variant="filled"
                value="Součet administrativních poplatků"
                disabled
                className="!w-full !bg-gray-50 !text-gray-500"
              />
            </Col>
            <Col xs={16} md={7} xl={8}>
              <InputNumber
                variant="filled"
                formatter={inputNumberFormatter}
                disabled
                value={processCostsTotal}
                addonAfter="Kč"
                className="!w-full !bg-gray-50"
              />
            </Col>
            <Col xs={8} md={4} xl={3}>
              <Button
                type="primary"
                ghost
                block
                onClick={() =>
                  add({
                    id: uuidv4(),
                    name: '',
                    amount: 0,
                  })
                }
                icon={<PlusOutlined />}
              />
            </Col>
          </Row>
        </>
      )}
    </Form.List>
  )
}
